import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import PropTypes from 'prop-types';

import { Anchor, Image } from '@zillow/constellation';

import stringHelper from 'app/shared/utils/stringHelper';
import analyticsUtils from 'app/client/utils/analyticsUtils';
import ClaimActions from 'app/shared/actions/ClaimActions';
import AnalyticsConstants from 'app/shared/constants/AnalyticsConstants';

import { RMW_PATH } from 'app/shared/pathsManifest';

import WizardBodyTemplate from 'app/shared/components/WizardBodyTemplate';
import WizardActionFooter from 'app/shared/components/WizardActionFooter';
import ViewClaimListingsModal from 'app/shared/components/Modal/ViewClaimListingsModal';
import PersonProfile from 'app/shared/images/PersonProfile.svg';

const { pluralize } = stringHelper;

const propTypes = {
    onGoToPrevStep: PropTypes.func.isRequired,
    onGoToNextStep: PropTypes.func.isRequired,
};

const defaultProps = {};

const WizVerifyContactSuccess = ({
    onGoToNextStep,
    onGoToPrevStep,
}) => {
    const dispatch = useDispatch();
    const [isTransitioning, setIsTransitioning] = useState(true);
    const [hasError, setHasError] = useState(false);
    const [listingCount, setListingCount] = useState('');
    const [contactMethodsCount, setContactMethodsCount] = useState('');
    const [showViewListingsModal, setShowViewListingsModal] = useState(false);

    const handleClickPrevStep = () => {
        dispatch(analyticsUtils.track.GAEventV2(
            AnalyticsConstants.GA_EVENTS.CLAIM_LISTINGS_CLICK_BACK,
            { label: AnalyticsConstants.CLAIM_LISTINGS_LABEL.CONTACT_VERIFICATION_SUCCESSFUL },
        ));
        onGoToPrevStep();
    };

    const handleClickNextStep = () => {
        dispatch(analyticsUtils.track.GAEventV2(
            AnalyticsConstants.GA_EVENTS.CLAIM_LISTINGS_CLICK_NEXT,
            { label: AnalyticsConstants.CLAIM_LISTINGS_LABEL.CONTACT_VERIFICATION_SUCCESSFUL },
        ));
        onGoToNextStep();
    };

    const handleViewListings = () => {
        dispatch(analyticsUtils.track.GAEventV2(
            AnalyticsConstants.GA_EVENTS.CLAIM_LISTINGS_CLICK,
            {
                label: AnalyticsConstants.CLAIM_LISTINGS_LABEL.CONTACT_VERIFICATION_SUCCESSFUL,
                action: 'ViewListings',
            },
        ));
        setShowViewListingsModal(true);
    };

    useEffect(() => {
        setIsTransitioning(true);
        dispatch(ClaimActions.propertyLookupAll({ includeUnverified: false }))
            .then((response) => {
                const {
                    totalContactMethodCount, totalListingCount,
                } = response;
                setListingCount(totalListingCount);
                setContactMethodsCount(totalContactMethodCount);
            }).catch(() => setHasError(true))
            .finally(() => setIsTransitioning(false));
    }, []);

    const handleLearnMoreClick = () => {
        window.open(RMW_PATH.LISTING_CLAIMING_HELP, '_blank');
    };

    const description = (
        <>
            We&apos;ll continue searching for listings with this contact information and alert you with new verified
            listings in your Zillow Rental Manager dashboard.
            {false && (
                <span>
                    {' '}
                    <Anchor onClick={handleLearnMoreClick}><b>Learn More</b></Anchor>
                </span>
            )}
        </>
    );

    return (
        <WizardBodyTemplate
            title={`You've claimed ${pluralize(listingCount, 'listing', 'listings')}`}
            subtitle={(
                <>
                    Congratulations! You have verified
                    {' '}
                    {pluralize(contactMethodsCount, 'contact', 'contacts')}
                    {' '}
                    and we&apos;ve linked
                    {' '}
                    {pluralize(listingCount, 'listing', 'listings')}
                    {' '}
                    to your account.
                    {' '}
                    <Anchor onClick={handleViewListings}><b>View Your Listings</b></Anchor>
                </>
            )
            }
            description={description}
            isTransitioning={isTransitioning}
            hasError={hasError}
            icon={<Image src={PersonProfile} />}
        >
            <WizardActionFooter
                onBackClick={handleClickPrevStep}
                onNextClick={handleClickNextStep}
                isBackDisabled={!onGoToPrevStep}
                isNextDisabled={listingCount < 1}
            />
            {showViewListingsModal && (
                <ViewClaimListingsModal
                    onClose={() => setShowViewListingsModal(false)}
                />
            )}
        </WizardBodyTemplate>
    );
};

WizVerifyContactSuccess.propTypes = propTypes;
WizVerifyContactSuccess.defaultProps = defaultProps;

export default WizVerifyContactSuccess;
